/**
 * Proyeccion Page — 12-month expandable rows view
 * src/pages/Proyeccion.jsx
 */

import React, { useState, useEffect, useMemo } from 'react';
import { collection, getDocs } from 'firebase/firestore';
import { ChevronDown, ChevronRight } from 'lucide-react';

const fmt = (n) => new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(n || 0);

const MONTHS_SHORT = ['Ene','Feb','Mar','Abr','May','Jun','Jul','Ago','Sep','Oct','Nov','Dic'];

const mxnBadge = { fontSize: '0.7rem', background: 'rgba(16,185,129,0.12)', color: '#10b981', borderRadius: '0.35rem', padding: '0.2rem 0.5rem', fontWeight: 700, verticalAlign: 'middle', marginLeft: '0.5rem' };

function getShortLabel(baseDate, offset) {
  const d = new Date(baseDate.getFullYear(), baseDate.getMonth() + offset, 1);
  return `${MONTHS_SHORT[d.getMonth()]} ${d.getFullYear().toString().slice(2)}`;
}

const NOW = new Date();

export default function Proyeccion({ db }) {
  const [ingresos, setIngresos] = useState([]);
  const [gastos,   setGastos]   = useState([]);
  const [pasivos,  setPasivos]  = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [expanded, setExpanded] = useState({ ingresos: false, gastos: false, deudas: false });

  useEffect(() => {
    (async () => {
      try {
        const [ingSnap, gasSnap, pasSnap] = await Promise.all([
          getDocs(collection(db, 'ingresos')),
          getDocs(collection(db, 'gastos')),
          getDocs(collection(db, 'pasivos')),
        ]);
        setIngresos(ingSnap.docs.map(d => ({ id: d.id, ...d.data() })));
        setGastos(gasSnap.docs.map(d => ({ id: d.id, ...d.data() })));
        setPasivos(pasSnap.docs.map(d => ({ id: d.id, ...d.data() })));
      } catch (err) { console.error(err); }
      finally { setLoading(false); }
    })();
  }, [db]);

  const ingresosMensuales = useMemo(() =>
    ingresos.reduce((s, r) => s + ((r.bruto || 0) - (r.impuestos || 0)), 0),
    [ingresos]
  );

  const gastosMensuales = useMemo(() =>
    gastos.reduce((s, g) => s + (g.monto || 0), 0),
    [gastos]
  );

  const gastosEsenciales = useMemo(() =>
    gastos.filter(g => g.esencial === 'Si').reduce((s, g) => s + (g.monto || 0), 0),
    [gastos]
  );

  const gastosNoEsenciales = useMemo(() =>
    gastos.filter(g => g.esencial !== 'Si').reduce((s, g) => s + (g.monto || 0), 0),
    [gastos]
  );

  const totalPasivosMensual = useMemo(() =>
    pasivos.filter(p => (p.pagoMensual || 0) > 0).reduce((s, p) => s + (p.pagoMensual || 0), 0),
    [pasivos]
  );

  // Build 12-month projection with pasivos simulation
  const monthData = useMemo(() => {
    let pasivosSim = pasivos.map(p => ({ ...p }));
    let balance = 0;
    return Array.from({ length: 12 }, (_, i) => {
      const label = getShortLabel(NOW, i);

      const pasivosPago = pasivosSim.reduce((s, p) => {
        if ((p.monto || 0) <= 0 || !p.pagoMensual) return s;
        return s + Math.min(p.pagoMensual, p.monto);
      }, 0);

      pasivosSim = pasivosSim.map(p => ({
        ...p,
        monto: Math.max(0, (p.monto || 0) - (p.pagoMensual || 0)),
      }));

      const net = ingresosMensuales - gastosMensuales - pasivosPago;
      balance += net;

      return { label, ingresos: ingresosMensuales, gastos: gastosMensuales, deudas: Math.round(pasivosPago), net: Math.round(net), balance: Math.round(balance) };
    });
  }, [pasivos, ingresosMensuales, gastosMensuales]);

  // Deudas sub-rows per pasivo with pagoMensual > 0
  const deudasSubRows = useMemo(() => {
    return pasivos.filter(p => (p.pagoMensual || 0) > 0).map(p => {
      const values = Array.from({ length: 12 }, (_, i) => {
        const mesesRestantes = p.meses || Math.ceil((p.monto || 0) / (p.pagoMensual || 1));
        return i < mesesRestantes ? p.pagoMensual : 0;
      });
      return { label: `${p.person} · ${p.tipo}`, values };
    });
  }, [pasivos]);

  if (loading) return (
    <div className="page-container">
      <div className="app-loading" style={{ minHeight: '40vh' }}><div className="spinner" /></div>
    </div>
  );

  const toggle = (key) => setExpanded(prev => ({ ...prev, [key]: !prev[key] }));

  const monthLabels = monthData.map(m => m.label);

  const thStyle = {
    fontSize: '0.68rem', fontWeight: 700, color: 'var(--text-secondary)',
    padding: '0.5rem 0.5rem', textAlign: 'right', whiteSpace: 'nowrap',
    background: 'var(--bg-secondary)', borderBottom: '1px solid var(--border-color)',
    minWidth: '80px',
  };
  const thFirstStyle = {
    ...thStyle, textAlign: 'left', position: 'sticky', left: 0, zIndex: 2,
    minWidth: '130px', maxWidth: '130px',
  };
  const tdStyle = { padding: '0.45rem 0.5rem', textAlign: 'right', fontSize: '0.78rem', whiteSpace: 'nowrap', borderBottom: '1px solid var(--border-color)' };
  const tdFirstStyle = {
    ...tdStyle, textAlign: 'left', position: 'sticky', left: 0, zIndex: 1,
    background: 'var(--bg-secondary)', borderRight: '1px solid var(--border-color)',
    fontWeight: 600, fontSize: '0.8rem', minWidth: '130px', maxWidth: '130px',
  };
  const tdSubFirstStyle = {
    ...tdFirstStyle, fontWeight: 400, fontSize: '0.72rem', color: 'var(--text-secondary)',
    paddingLeft: '1.5rem',
  };

  return (
    <div className="page-container">
      <h1 className="page-title">Proyección — 12 Meses <span style={mxnBadge}>MXN</span></h1>

      {/* Summary cards */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '0.625rem', marginBottom: '1.25rem' }}>
        {[
          { label: 'Ingresos Neto/Mes',  value: ingresosMensuales, color: '#10b981' },
          { label: 'Gastos Presupuesto', value: gastosMensuales,   color: '#ef4444' },
          { label: 'Pagos Pasivos/Mes',  value: totalPasivosMensual, color: '#f59e0b' },
        ].map(({ label, value, color }) => (
          <div key={label} className="card" style={{ padding: '0.625rem 0.75rem', borderLeft: `3px solid ${color}` }}>
            <div style={{ fontSize: '0.65rem', color: 'var(--text-secondary)', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.03em' }}>{label}</div>
            <div style={{ fontSize: '0.95rem', fontWeight: 700, color, marginTop: '0.1rem' }}>{fmt(value)}</div>
          </div>
        ))}
      </div>

      {/* Expandable table */}
      <div style={{ overflowX: 'auto', borderRadius: '0.875rem', border: '1px solid var(--border-color)', background: 'var(--bg-secondary)' }}>
        <table style={{ borderCollapse: 'collapse', width: '100%', minWidth: '900px' }}>
          <thead>
            <tr>
              <th style={thFirstStyle}>Categoría</th>
              {monthLabels.map(l => <th key={l} style={thStyle}>{l}</th>)}
            </tr>
          </thead>
          <tbody>
            {/* INGRESOS row */}
            <tr style={{ background: 'rgba(16,185,129,0.04)', cursor: 'pointer' }} onClick={() => toggle('ingresos')}>
              <td style={{ ...tdFirstStyle, background: 'rgba(16,185,129,0.06)', color: '#10b981' }}>
                <span style={{ display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
                  {expanded.ingresos ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                  Ingresos
                </span>
              </td>
              {monthData.map((m, i) => (
                <td key={i} style={{ ...tdStyle, color: '#10b981', fontWeight: 700 }}>{fmt(m.ingresos)}</td>
              ))}
            </tr>
            {expanded.ingresos && ingresos.map((r, ri) => {
              const neto = (r.bruto || 0) - (r.impuestos || 0);
              return (
                <tr key={ri} style={{ background: 'rgba(16,185,129,0.02)' }}>
                  <td style={tdSubFirstStyle}>{r.person} · {r.tipo}</td>
                  {Array(12).fill(null).map((_, i) => (
                    <td key={i} style={{ ...tdStyle, color: '#10b981', fontSize: '0.72rem' }}>{fmt(neto)}</td>
                  ))}
                </tr>
              );
            })}

            {/* GASTOS row */}
            <tr style={{ background: 'rgba(239,68,68,0.04)', cursor: 'pointer' }} onClick={() => toggle('gastos')}>
              <td style={{ ...tdFirstStyle, background: 'rgba(239,68,68,0.06)', color: '#ef4444' }}>
                <span style={{ display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
                  {expanded.gastos ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                  Gastos
                </span>
              </td>
              {monthData.map((m, i) => (
                <td key={i} style={{ ...tdStyle, color: '#ef4444', fontWeight: 700 }}>{fmt(m.gastos)}</td>
              ))}
            </tr>
            {expanded.gastos && (
              <>
                <tr style={{ background: 'rgba(239,68,68,0.02)' }}>
                  <td style={tdSubFirstStyle}>Esenciales</td>
                  {Array(12).fill(null).map((_, i) => (
                    <td key={i} style={{ ...tdStyle, color: '#10b981', fontSize: '0.72rem' }}>{fmt(gastosEsenciales)}</td>
                  ))}
                </tr>
                <tr style={{ background: 'rgba(239,68,68,0.02)' }}>
                  <td style={tdSubFirstStyle}>No Esenciales</td>
                  {Array(12).fill(null).map((_, i) => (
                    <td key={i} style={{ ...tdStyle, color: '#6b7280', fontSize: '0.72rem' }}>{fmt(gastosNoEsenciales)}</td>
                  ))}
                </tr>
              </>
            )}

            {/* DEUDAS row */}
            <tr style={{ background: 'rgba(245,158,11,0.04)', cursor: 'pointer' }} onClick={() => toggle('deudas')}>
              <td style={{ ...tdFirstStyle, background: 'rgba(245,158,11,0.06)', color: '#f59e0b' }}>
                <span style={{ display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
                  {expanded.deudas ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                  Deudas
                </span>
              </td>
              {monthData.map((m, i) => (
                <td key={i} style={{ ...tdStyle, color: m.deudas > 0 ? '#f59e0b' : 'var(--text-light)', fontWeight: m.deudas > 0 ? 700 : 400 }}>{fmt(m.deudas)}</td>
              ))}
            </tr>
            {expanded.deudas && deudasSubRows.map((sr, ri) => (
              <tr key={ri} style={{ background: 'rgba(245,158,11,0.02)' }}>
                <td style={tdSubFirstStyle}>{sr.label}</td>
                {sr.values.map((v, i) => (
                  <td key={i} style={{ ...tdStyle, color: v > 0 ? '#f59e0b' : 'var(--text-light)', fontSize: '0.72rem' }}>{v > 0 ? fmt(v) : '—'}</td>
                ))}
              </tr>
            ))}
            {expanded.deudas && deudasSubRows.length === 0 && (
              <tr>
                <td style={{ ...tdSubFirstStyle, fontStyle: 'italic' }}>Sin deudas con pago mensual</td>
                {Array(12).fill(null).map((_, i) => <td key={i} style={tdStyle}>—</td>)}
              </tr>
            )}

            {/* NETO DEL MES row */}
            <tr style={{ background: 'var(--bg-tertiary)' }}>
              <td style={{ ...tdFirstStyle, fontWeight: 700, fontSize: '0.82rem', color: 'var(--text-primary)' }}>Neto del Mes</td>
              {monthData.map((m, i) => (
                <td key={i} style={{ ...tdStyle, fontWeight: 700, color: m.net >= 0 ? '#10b981' : '#ef4444' }}>{fmt(m.net)}</td>
              ))}
            </tr>

            {/* BALANCE ACUMULADO row */}
            <tr style={{ background: 'var(--bg-secondary)' }}>
              <td style={{ ...tdFirstStyle, fontWeight: 700, fontSize: '0.82rem', color: 'var(--text-primary)', borderBottom: 'none' }}>Balance Acum.</td>
              {monthData.map((m, i) => (
                <td key={i} style={{ ...tdStyle, fontWeight: 700, color: m.balance >= 0 ? '#10b981' : '#ef4444', borderBottom: 'none' }}>{fmt(m.balance)}</td>
              ))}
            </tr>
          </tbody>
        </table>
      </div>

      <p style={{ marginTop: '0.75rem', fontSize: '0.75rem', color: 'var(--text-light)', textAlign: 'center' }}>
        Haz clic en Ingresos, Gastos o Deudas para expandir el detalle. Los valores de Deudas se calculan en base a pasivosMensuales configurados en Pasivos.
      </p>
    </div>
  );
}
